import {
    AppId,
    equalInLC,
    FCX,
    getRem, sanitize,
    usePlatformContext,
    usePlatformFeatureFlag
} from "@datassential/platform-ui-lib";
import { BsStars } from "react-icons/bs";
import { IconArrowTop } from "@icons";
import AiResult from '@vms/AiResult';
import Collapsable from '@components/Collapsable';
import Divider from '@components/Divider';
import useQueryState from "@hooks/useQueryState";
import { FEATURE_AI_SEARCH, FEATURE_AI_SEARCH_DS_AI, AI_SEARCH_QUERY_PARAM, AI_SEARCH_ANSWER_PARAM } from "@constants/general";
import { useAiSearchSummary } from "@apiHooks/useAiSearchSummary";
import { useAiSearchSettingsContext } from "@vms/AiSearchSettingsContext/AiSearchSettingsContext";
import { SettingsItem } from "@vms/AiResult/AiResultSettings";
import { ReactNode, useMemo } from "react";

const AI_SUMMARY_REPLACEMENT_PLACEHOLDER_TEXT = "redirected";

const AiResultSection: FCX<{
    isSettingsLoading: boolean;
    settings: SettingsItem[][];
}> = ({
    settings,
    isSettingsLoading,
}) => {
    const {
        userInfo: {
            products,
            reportProMoreResultsUrl,
        },
        isUserInfoLoaded,
    } = usePlatformContext();
    const [search] = useQueryState(AI_SEARCH_QUERY_PARAM, '');
    const [searchAnswer] = useQueryState(AI_SEARCH_ANSWER_PARAM, '');

    const query = search || searchAnswer;

    const isFeatureEnabled = usePlatformFeatureFlag(FEATURE_AI_SEARCH);
    const isDsAiSearchEnabled = usePlatformFeatureFlag(FEATURE_AI_SEARCH_DS_AI);
    // Same run that produced the settings above — the stream is owned by the
    // provider mounted over the router, so nothing is re-requested here.
    const stream = useAiSearchSettingsContext();
    const legacy = useAiSearchSummary(isUserInfoLoaded && !isDsAiSearchEnabled ? query : '');

    const {
        summary,
        isSummaryLoading,
        isRelatedToReportPro,
        isRelatedToConsumerPreferences,
    } = isDsAiSearchEnabled
        ? {
            summary: stream.answer,
            isSummaryLoading: stream.isAnswerLoading,
            isRelatedToReportPro: stream.isRelatedToReportPro,
            isRelatedToConsumerPreferences: stream.isRelatedToConsumerPreferences,
        }
        : {
            ...legacy.data,
            isSummaryLoading: legacy.isLoading,
        };

    const consumerPreferences = useMemo(
        () => products[AppId.ConsumerPreferences],
        [products]
    );

    const reportPro = useMemo(
        () => products[AppId.ReportPro],
        [products]
    );

    const summaryNode = useMemo(
        (): ReactNode | undefined => {
            if (!isDsAiSearchEnabled && !!summary && !equalInLC(summary, AI_SUMMARY_REPLACEMENT_PLACEHOLDER_TEXT)) return undefined;
            const isConsumerPreferencesBulletShown = isRelatedToConsumerPreferences && !(consumerPreferences?.isDisabled ?? true);
            const isReportProBulletShown = isRelatedToReportPro && !(reportPro?.isDisabled ?? true);

            // A cross-product question gets the branded hand-off instead of the
            // raw answer, which names an internal service. Anything else with an
            // answer renders it as-is; only a genuinely empty result falls
            // through to the apology below.
            if (isDsAiSearchEnabled && !isConsumerPreferencesBulletShown && !isReportProBulletShown && !!summary) return undefined;

            return (
                <ul>
                    {isConsumerPreferencesBulletShown && (
                        <li>
                            The cooks over at Consumer Preferences can serve up a better answer for this: click&nbsp;
                            <a
                                className="AiResult__link"
                                href={sanitize(`${consumerPreferences?.url}?search=${encodeURIComponent(query)}`)}
                                target="_blank"
                                rel="noreferrer"
                            >
                                HERE
                            </a>
                            &nbsp;to go to {consumerPreferences?.name}
                        </li>
                    )}
                    {isReportProBulletShown && (
                        <li>
                            Let's let {reportPro?.name} cook up the answer to this. Click&nbsp;
                            <a
                                className="AiResult__link"
                                href={sanitize(`${reportPro?.url}/${reportProMoreResultsUrl}${encodeURIComponent(query)}`)}
                                target="_blank"
                                rel="noreferrer"
                            >
                                HERE
                            </a>
                            &nbsp;to access our library of reports
                        </li>
                    )}
                    {!isConsumerPreferencesBulletShown && !isReportProBulletShown && (
                        <li>
                            We’ve hit a little jam; stay toasty while we get up and running again
                        </li>
                    )}
                </ul>
            );
        },
        [
            isDsAiSearchEnabled,
            summary,
            isRelatedToConsumerPreferences,
            isRelatedToReportPro,
            consumerPreferences,
            reportPro,
            reportProMoreResultsUrl,
            query,
        ]
    );

    if (!isFeatureEnabled) return null;
    
    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: getRem(20) }}>
            <Collapsable defaultShow>
                <Collapsable.Content>
                    <AiResult
                        className="appears-top"
                        question={query}
                        answer={summaryNode ? undefined : summary}
                        answerNode={summaryNode}
                        isAnswerLoading={isSummaryLoading}
                        settings={settings}
                        isSettingsLoading={isSettingsLoading}
                    />
                </Collapsable.Content>
                <Divider
                    className="appears-top del-2"
                    items={[
                        null,
                        <Collapsable.Control
                            renderLabel={(show) => (
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: getRem(8),
                                    fontSize: getRem(12),
                                }}>
                                    <BsStars style={{
                                        width: getRem(22),
                                        height: getRem(22),
                                    }}/>
                                    {show ? "Hide" : "Show"}&nbsp;AI Summary
                                    <IconArrowTop
                                        style={{
                                            transform: show ? 'rotate(0deg)' : 'rotate(180deg)',
                                        }}
                                    />
                                </div>
                            )}
                        />
                    ]}
                />
            </Collapsable>
        </div>
    );
};

export default AiResultSection;
